/**
 * LibraryView.jsx — Carousel Composer light table: search, family chips,
 * thumbnail size, the contact sheet, the drop zone and (while choosing a photo
 * for a slide) the assign bar. One screen; only the middle row scrolls.
 *
 * On a phone this is the Pick step: the two photo sources move into the filter
 * bar (there is nothing to drop a folder onto), and the foot carries the
 * selection, the add-style and the way on to Review.
 *
 * Choosing a replacement for a cell brings a small preview of the slide you
 * came from with you — in the assign bar on desktop, floating over the foot on
 * a phone — so you can see which cell you are filling, and tap it to go back.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  cancelAssign, clearFilters, loadFiles, selectFamCounts, selectImgById, selectSaved,
  selectSelIndex, selectUsed, selectVisible, setFilters, setSize, toggleFam, uiMemory, useCarousel,
} from '../../hooks/useCarouselStore';
import { useMobile } from '../../hooks/useMobile';
import { cellImgId, cellRoleName, formatSize, slideCells, slideGeom } from '../../lib/carousel/geometry.js';
import { chipCounts, groupRows, hasActiveFilters, libInfoText } from '../../lib/carousel/library.js';
import { renderTile, useTileHost } from './PhotoTile';
import ComposerBar from './ComposerBar';
import SlideStage from './SlideStage';
import { scrollPageTop } from './dom.js';

function Chip({ label, n, on, onClick }) {
  return (
    <button type="button" className="chip" aria-pressed={on ? 'true' : 'false'} onClick={onClick}>
      {label}
      {n ? <span className="n">{n}</span> : null}
    </button>
  );
}

// The slide the swap came from, with the cell being filled outlined. The outline
// is the same rectangle slideCells() hands the stage, so it lands on any layout.
function SwapPreview({ sl, si, ci, design, panos, imgById, cur, float }) {
  const [W, H] = formatSize(design.fmt);
  const g = slideGeom(sl, design);
  const rects = slideCells(sl, W, H, g.pad, g.gap);
  const rc = rects[ci] || rects[0];
  const mark = {
    left: (rc.x / W) * 100 + '%',
    top: (rc.y / H) * 100 + '%',
    width: (rc.w / W) * 100 + '%',
    height: (rc.h / H) * 100 + '%',
  };
  return (
    <button
      type="button"
      className={'cc-swap' + (float ? ' is-float' : '')}
      title="Back to the slide"
      onClick={cancelAssign}
    >
      <span className="cc-swap-stage">
        <SlideStage
          sl={sl}
          si={si}
          design={design}
          group={sl.pano ? panos[sl.pano.gid] : null}
          imgById={imgById}
          interactive={false}
        />
        <span className="cc-swap-mark" style={mark} aria-hidden="true" />
      </span>
      <span className="cc-swap-txt">
        <span className="lbl">{'Slide ' + String(si + 1).padStart(2, '0') + ' · ' + cellRoleName(sl, ci)}</span>
        <span className="cc-swap-now">{cur ? cur.name : 'Empty cell'}</span>
        <span className="hint">Tap to go back</span>
      </span>
    </button>
  );
}

export default function LibraryView({ onChoose, onShoots, onStep }) {
  const { isMobile } = useMobile();
  const library = useCarousel((s) => s.library);
  const sel = useCarousel((s) => s.sel);
  const filters = useCarousel((s) => s.filters);
  const size = useCarousel((s) => s.size);
  const focusId = useCarousel((s) => s.focusId);
  const assign = useCarousel((s) => s.assign);
  const slides = useCarousel((s) => s.slides);
  const panos = useCarousel((s) => s.panos);
  const design = useCarousel((s) => s.design);
  const loading = useCarousel((s) => s.loading);
  const shown = useCarousel(selectVisible);
  const famCounts = useCarousel(selectFamCounts);
  const used = useCarousel(selectUsed);
  const selIndex = useCarousel(selectSelIndex);
  const imgById = useCarousel(selectImgById);
  const saved = useCarousel(selectSaved);
  const draftPrompt = useCarousel((s) => s.draftPrompt);

  const scrollRef = useRef(null);
  const searchRef = useRef(null);
  const [over, setOver] = useState(false);
  const [qRaw, setQRaw] = useState(filters.q);
  const host = useTileHost();

  useEffect(() => {
    setQRaw((r) => (r.trim() === filters.q ? r : filters.q));
  }, [filters.q]);

  // entering the library: the page scrolls to the top, the sheet comes back where it was
  useLayoutEffect(() => {
    scrollPageTop(false);
    if (scrollRef.current) scrollRef.current.scrollTop = uiMemory.sheetTop || 0;
  }, []);

  const rows = useMemo(
    () => groupRows(shown, { searching: !!filters.q, famCounts }),
    [shown, filters.q, famCounts],
  );
  const chips = useMemo(() => chipCounts(library, used, famCounts), [library, used, famCounts]);

  const assignSl = assign ? slides[assign.si] : null;
  const curId = assignSl ? cellImgId(assignSl, assign.ci, panos, imgById) : null;
  const t = { selIndex, focusId, curId, used };
  const swap = assignSl ? (
    <SwapPreview
      sl={assignSl}
      si={assign.si}
      ci={assign.ci}
      design={design}
      panos={panos}
      imgById={imgById}
      cur={curId ? imgById(curId) : null}
      float={isMobile}
    />
  ) : null;

  const onDropZone = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    if (e.dataTransfer.files.length) loadFiles(e.dataTransfer.files);
  };

  return (
    <section className="library">
      <div className="libbar">
        {isMobile && (
          <div className="cc-group cc-msrc">
            <button type="button" className="btn tiny" onClick={onChoose}>Add photos</button>
            <button type="button" className="btn tiny" onClick={onShoots} title="Load a delivered shoot">Shoots</button>
          </div>
        )}
        <label className="search">
          <input
            ref={searchRef}
            type="search"
            data-cc-search="1"
            placeholder="Find a frame — 9418, dji 0471, edit"
            value={qRaw}
            onChange={(e) => { setQRaw(e.target.value); setFilters({ q: e.target.value.trim() }); }}
          />
          {filters.q && (
            <button
              type="button"
              className="btn tiny ghost"
              aria-label="Clear search"
              onClick={() => { setFilters({ q: '' }); searchRef.current?.focus(); }}
            >
              ✕
            </button>
          )}
        </label>
        <div className="chips">
          {library.length > 0 && (
            <>
              {chips.fams.map((c) => (
                <Chip key={c.key} label={c.key} n={c.n} on={filters.fams.indexOf(c.key) >= 0} onClick={() => toggleFam(c.key)} />
              ))}
              {chips.unused > 0 && chips.unused < library.length && (
                <Chip label="Unused" n={chips.unused} on={filters.unused} onClick={() => setFilters({ unused: !filters.unused })} />
              )}
              {chips.edits > 0 && (
                <Chip label="Edits" n={chips.edits} on={filters.edits} onClick={() => setFilters({ edits: !filters.edits })} />
              )}
              {sel.length > 0 && (
                <Chip label="Selected" n={sel.length} on={filters.onlySel} onClick={() => setFilters({ onlySel: !filters.onlySel })} />
              )}
              {hasActiveFilters(filters) && <Chip label="✕ Clear" n={0} on={false} onClick={clearFilters} />}
            </>
          )}
        </div>
        <div className="seg size-seg">
          {['S', 'M', 'L', 'XL'].map((label, i) => (
            <button key={label} type="button" aria-pressed={size === i} onClick={() => setSize(i)}>{label}</button>
          ))}
        </div>
        <span className="lbl libinfo">
          {loading ? 'Loading ' + loading.done + ' / ' + loading.total : libInfoText(library, sel, shown)}
        </span>
      </div>

      <div
        className="sheet-scroll"
        ref={scrollRef}
        onScroll={(e) => { uiMemory.sheetTop = e.currentTarget.scrollTop; host.onScroll(); }}
      >
        <div
          className="sheet-grid"
          data-size={size}
          role="listbox"
          aria-multiselectable="true"
          tabIndex={0}
          onClick={host.onClick}
          onDoubleClick={host.onDoubleClick}
          onDragStart={host.onDragStart}
          onPointerDown={host.onPointerDown}
        >
          {rows.map((r, i) => {
            if (r.t === 'tile') return renderTile(r.im, t);
            if (r.t === 'head') {
              return (
                <div key={'h:' + r.key} className="g-head">
                  <span className="lbl">{r.key + ' · ' + r.n}</span>
                  <button type="button" className="btn tiny ghost" data-selectall={r.key}>Select all</button>
                </div>
              );
            }
            return (
              <div key={'r:' + (rows[i + 1] && rows[i + 1].im ? rows[i + 1].im.id : i)} className="g-run">
                <span>{r.label}</span>
              </div>
            );
          })}
        </div>
        {library.length > 0 && !shown.length && (
          <p className="sheet-none">
            No frames match. <button type="button" className="btn tiny ghost" onClick={clearFilters}>Clear filters</button>
          </p>
        )}
        {saved && !library.length && !draftPrompt && (
          <p className="lib-restore">
            {'Your last carousel (' + saved.slides.length + ' slides) is saved — drop the same photos and it comes back.'}
          </p>
        )}
        <div
          className={'drop' + (over ? ' over' : '')}
          onClick={onChoose}
          onDragEnter={(e) => { e.preventDefault(); setOver(true); }}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setOver(false); }}
          onDrop={onDropZone}
        >
          <strong>{isMobile ? 'Add photos from this phone' : 'Drop your photos here'}</strong>
          {isMobile ? 'or load a delivered shoot above' : 'or click to choose files'}
        </div>
        <ol className="howto">
          <li><b>Add photos</b> — drop a folder or pick files. They are read in your browser and never uploaded.</li>
          <li><b>Build slides</b> — select frames, then pick Single, Stacked, Side by side, Hero + 3 or Panorama.</li>
          <li><b>Export</b> — save every slide at up to 2160 px, named in carousel order.</li>
        </ol>
      </div>

      <div className="libfoot">
        {isMobile && swap}
        <ComposerBar />
        {isMobile && !assign && (
          <div className="cc-mfwd">
            <span className="lbl cc-mcount">
              {loading ? 'Loading ' + loading.done + ' / ' + loading.total : libInfoText(library, sel, shown)}
            </span>
            <button type="button" className="btn primary cc-mnext" onClick={() => onStep('review')}>
              {'Review' + (slides.length ? ' ' + slides.length : '') + ' ›'}
            </button>
          </div>
        )}
      </div>
      {assign && (
        <div className="assignbar">
          {/* which slide and cell is the preview's job, so the bar only prompts */}
          {!isMobile && swap}
          <span className="hint">Tap a frame to use it</span>
          <button type="button" className="btn tiny ghost" onClick={cancelAssign}>Cancel</button>
        </div>
      )}
    </section>
  );
}
